/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

/**
 * This file includes utility functions used throughout the entire application
 */

import isNil from 'lodash/isNil';
import { DEFAULT_NETWORK_GROUP_ID } from '../components/common/map_overlays/overlaysDefn';
import { ThemeInterface } from '../components/common/theme/AppThemeInterface';
import appTheme from '../components/common/theme/appTheme.json';
import { SpecialBeamsNetworkMappingType } from '../shared/entities/config';
import { useEffect } from 'react';

const theme: ThemeInterface = appTheme;

// 12.5 hours in seconds => 12.5 hours x 60 minutes x 60 seconds.
// Note: Added 0.5 hours as a buffer to ensure the connection established annotation will show on the timeline.
export const STALE_DATA_GAP_SEC = 45000; 

/**
 * Returns the Network Group Id (Ka, Ku, HCX) based on
 * the beam Id and the SPECIAL_BEAMS_NETWORK_MAPPING
 */
export const getNetworkGroupIdByBeamId = (
  specialBeamsNetworkMapping: SpecialBeamsNetworkMappingType,
  beamId: string,
): string => {
  let networkGroupId = null;

  if (!isNil(specialBeamsNetworkMapping)) {
    networkGroupId = Object.keys(specialBeamsNetworkMapping).find((key: string) =>
      specialBeamsNetworkMapping[key]?.includes(beamId),
    );
  }

  return !isNil(networkGroupId) ? networkGroupId : DEFAULT_NETWORK_GROUP_ID;
};

/**
 * Returns the Network Group Color (color for either Ka, Ku, or HCX)
 * based on the beam Id, the SPECIAL_BEAMS_NETWORK_MAPPING, and
 * the app css configuration
 */
export const getNetworkGroupColorByBeamId = (
  specialBeamsNetworkMapping: SpecialBeamsNetworkMappingType,
  beamId: string,
): string => {
  const networkGroupId = getNetworkGroupIdByBeamId(specialBeamsNetworkMapping, beamId);

  return theme.overlayBeams.networkGroups[networkGroupId];
};

/**
 * Returns true if Insights in running in mock mode, otherwise returns false.
 */
export const isMockMode = () => (import.meta.env.VITE_INSIGHTS_API_MOCK === 'true');

/**
 * This custom hook function uses the useEffect React Hook to perform the given clickAwayAction function on the given refHtmlElem reference.
 * Any mousedown event on a HTML element other than the refHtmlElem will make a call to clickAwayAction.
 * Furthermore, this custom hook takes an optional list of HTML element selectors (closestElementSelectors) that
 * indicate which closest targets should be ignored for the mousedown event.
 *
 * How to use:
 *   const ref = useRef<HTMLElement>(null);
 *   useClickAwayListener(ref, () => {
 *     // do something
 *   }, ['#div1', '.classNameA', ...]));
 *
 * @param refHtmlElem HTML element reference
 * @param clickAwayAction function that performs the clickAway operation on the refHtmlElem
 * @param closestElementSelectors closest targets to ignore mousedown event in the form of HTML selector strings array
 */
export const useClickAwayListener = (
  refHtmlElem: React.RefObject<HTMLElement>,
  clickAwayAction: () => void,
  ...closestElementSelectors: string[]
): void => {
  useEffect(() => {
    const handleClickAway = (event: any) => {
      // Inner click
      if (!isNil(refHtmlElem) && !isNil(refHtmlElem.current) && refHtmlElem.current.contains(event.target)) {
        return;
      }

      // Outer click
      if (!isNil(event) && !isNil(event.target)) {
        // Perform the clickAwayAction if closestElementSelectors was passed as null, undefined, or empty array
        if (isNil(closestElementSelectors) || !closestElementSelectors.length) {
          clickAwayAction();
          return;
        }

        // Perform the clickAwayAction only if the event.target is not closest to any of the given closestElementSelectors
        if (!isNil(closestElementSelectors) && closestElementSelectors.length) {
          let closestToAnySelector = false;
          for (let i = 0; i < closestElementSelectors.length; i += 1) {
            const selector = closestElementSelectors[i];
            if ((event.target as HTMLElement).closest(selector)) {
              closestToAnySelector = true;
              break;
            }
          }

          if (!closestToAnySelector) {
            clickAwayAction();
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickAway);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
    };
  });
};
